import React, { useState } from 'react';
import {
  TILE_STATES,
  isClickable,
  showVoucherCallout,
  VOUCHER_CALLOUT,
} from '../solutions';

// AppCard — one SmartView solution tile.
// Props:
//   solution : a SOLUTIONS entry (id, name, icon, layer, description, blurb,
//              trialCode?{endpoint,placeholder}, launchLabel…)
//   state    : a TILE_STATES value (derived by Dashboard via deriveTileState)
//   gido     : the current GIDO doc (needed to POST gido_id on trial-code apply)
//   onClick  : invoked only when the tile is clickable (see isClickable)
//
// Colour + label are derived from state; clickability is authoritative from
// solutions.js (never re-derived here). Offered-but-unlicensed tiles carry an
// expandable "Learn more" that reveals a short blurb, the voucher callout, and
// — when the solution has a public trial-activation endpoint — a code box.
//
// IMPORTANT (known gap, logged 2026-07-22): activating a trial code does NOT
// currently write a licence grant on any solution (SmartFinance or SmartMail).
// A successful activation therefore does not flip this tile to licensed — the
// success message says so honestly instead of implying the tile will unlock.

function viewForState(solution, state, clickable) {
  switch (state) {
    case TILE_STATES.LICENSED_ACTIVE:
    case TILE_STATES.LICENSED_NO_DATA:
      return clickable
        ? { color: 'gold', label: 'OPEN' }
        : { color: 'blue', label: 'Ready' };
    case TILE_STATES.OFFERED_UNLICENSED:
      return { color: 'blue', label: null };   // the Learn-more toggle renders instead
    case TILE_STATES.COMING_DATED:
      return { color: 'grey', label: null };    // launch badge carries it
    case TILE_STATES.NOT_OFFERED:
    default:
      return { color: 'grey', label: 'Not available' };
  }
}

function TrialCodeBox({ solution, gido }) {
  const [code, setCode]       = useState('');
  const [applying, setApplying] = useState(false);
  const [status, setStatus]     = useState(null); // {ok, msg}

  async function apply() {
    if (!code.trim()) return;
    setApplying(true);
    setStatus(null);
    try {
      const res = await fetch(solution.trialCode.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gido_id: gido?.gido_id, code: code.trim() }),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        const days = data.trial_days
          ? `${data.trial_days}-day`
          : data.trial_expires ? '' : '';
        setStatus({
          ok: true,
          msg:
            `Trial activated${days ? ` (${days})` : ''}${data.trial_expires ? ` — runs until ${new Date(data.trial_expires).toLocaleDateString()}` : ''}. ` +
            'Full access is being wired up — we\u2019ll notify you when it opens.',
        });
      } else {
        const map = {
          invalid_code:        'That code isn\u2019t valid.',
          already_extended:    'This account has already used a trial code for this solution.',
          already_on_paid_tier:'This account is already on a paid plan.',
        };
        setStatus({ ok: false, msg: map[data.error] || 'Could not apply code.' });
      }
    } catch (e) {
      setStatus({ ok: false, msg: 'Network error. Try again.' });
    }
    setApplying(false);
  }

  return (
    <div className="app-trialcode-box">
      <div className="app-trialcode-row">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder={solution.trialCode.placeholder || 'Enter code'}
          className="app-trialcode-input"
          onClick={(e) => e.stopPropagation()}
        />
        <button
          type="button"
          className="app-trialcode-btn"
          onClick={(e) => { e.stopPropagation(); apply(); }}
          disabled={applying || !code.trim()}
        >
          {applying ? '...' : 'Apply'}
        </button>
      </div>
      {status && (
        <span className={status.ok ? 'app-trialcode-ok' : 'app-trialcode-err'}>{status.msg}</span>
      )}
    </div>
  );
}

function AppCard({ solution, state, gido, onClick }) {
  const [expanded, setExpanded] = useState(false);
  const clickable = isClickable(solution, state);
  const { color, label } = viewForState(solution, state, clickable);
  const isUnlicensed = state === TILE_STATES.OFFERED_UNLICENSED;

  const cardClass =
    `app-card ${color}` +
    (clickable ? ' clickable' : '') +
    (state === TILE_STATES.COMING_DATED ? ' coming' : '');

  const toggleLearnMore = (e) => {
    e.stopPropagation();
    setExpanded((v) => !v);
  };

  return (
    <div className={cardClass} onClick={clickable ? onClick : undefined}>
      <div className="app-icon">{solution.icon}</div>
      <div className="app-name">{solution.name}</div>
      <div className="app-layer">{solution.layer}</div>
      <div className="app-desc">{solution.description}</div>

      {state === TILE_STATES.COMING_DATED && solution.launchLabel && (
        <div className="app-launch-badge">{solution.launchLabel}</div>
      )}

      {state === TILE_STATES.COMING_DATED && solution.launchCta && (
        <a
          className="app-launch-cta"
          href={solution.launchCta.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {solution.launchCta.text}
        </a>
      )}

      {label && <div className={`app-status ${color}`}>{label}</div>}

      {isUnlicensed && (
        <button
          type="button"
          className="app-learnmore"
          onClick={toggleLearnMore}
          aria-expanded={expanded}
        >
          {expanded ? 'Show less \u25B4' : 'Learn more \u25BE'}
        </button>
      )}

      {isUnlicensed && expanded && (
        <>
          {solution.blurb && <div className="app-blurb">{solution.blurb}</div>}
          {showVoucherCallout(state) && (
            <div className="app-voucher-callout">
              Interested in testing this solution? Contact our Customer Success team
              for a test voucher at{' '}
              <a href={`mailto:${VOUCHER_CALLOUT.email}`}>{VOUCHER_CALLOUT.email}</a>.
            </div>
          )}
          {solution.trialCode && <TrialCodeBox solution={solution} gido={gido} />}
        </>
      )}
    </div>
  );
}

export default AppCard;